import type { Bootstrap } from "./bootstrap"

/** The uniform error envelope every /api/v1 endpoint answers failures with. */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> }
  request_id?: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {}
  ) {
    super(message)
    this.name = "ApiError"
  }
}

export interface ApiClient {
  get<T>(path: string, params?: Record<string, unknown>): Promise<T>
  send<T>(method: "POST" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T>
}

/**
 * The one place that knows how to talk to the server: protocol negotiation, the
 * CSRF token on writes, and turning the error envelope into something typed.
 *
 * Nothing above this layer calls `fetch`, so when reads start coming from the
 * device's own store instead, only the repository behind it changes.
 */
export function createApiClient(bootstrap: Bootstrap): ApiClient {
  const headers = () => ({
    "X-ERegister-Protocol": bootstrap.protocolVersion,
    Accept: "application/json"
  })

  async function unwrap<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T

    const payload = await response.json().catch(() => null)

    if (!response.ok) {
      const body = payload as ApiErrorBody | null
      throw new ApiError(
        response.status,
        body?.error?.code ?? "unknown",
        body?.error?.message ?? response.statusText,
        body?.error?.details ?? {}
      )
    }

    return payload as T
  }

  return {
    async get<T>(path: string, params: Record<string, unknown> = {}): Promise<T> {
      const url = new URL(path, window.location.origin)
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== "") url.searchParams.set(key, String(value))
      }

      return unwrap<T>(await fetch(url, { headers: headers(), credentials: "same-origin" }))
    },

    async send<T>(method: "POST" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
      // The session cookie rides along automatically, which is exactly why the
      // server demands this token on anything that writes.
      const response = await fetch(path, {
        method,
        credentials: "same-origin",
        headers: {
          ...headers(),
          "Content-Type": "application/json",
          "X-CSRF-Token": bootstrap.csrfToken
        },
        body: body === undefined ? undefined : JSON.stringify(body)
      })

      return unwrap<T>(response)
    }
  }
}
